/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useContext } from 'react';
import { truncate, replace } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { Tag, Header, Text, BrandColors, MaterialIcon } from '@blend/enterprise-components';
import { BlendBridge } from 'blb-types/blendBridge';
import { threadHasANewMessage, getReplyByNameAndTime } from '../../utils/threadUtils';
import { TAG_STATE } from '../../constants/threadConstants';
import type { ReduxProps } from './ThreadItemContainer';
import { useParams } from 'react-router-dom';
import { InboxContext } from '../Inbox/InboxContext';

const styles = StyleSheet.create({
  awaitingMsgBorder: {
    borderLeft: `3px solid ${BrandColors.ACCENT_YELLOW_70}`,
  },
  columnContentWrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: 20,
    paddingTop: 6,
  },
  columnCountWrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
  contentWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  countWrapper: {
    minWidth: 95,
    textAlign: 'right',
  },
  header: {
    marginRight: 8,
  },
  headerWrapper: {
    alignItems: 'center',
    display: 'flex',
  },
  newMsgBorder: {
    borderLeft: `3px solid ${BrandColors.ACCENT_SKY_70}`,
  },
  rowCountsWrapper: {
    display: 'flex',
  },
  textMarginBottom: {
    marginBottom: 6,
  },
  threadWrapper: {
    backgroundColor: BrandColors.WHITE,
    padding: '14px 14px',
  },
});

export namespace ThreadItem {
  export interface Props {
    thread: BlendBridge.API.Thread;
  }
}

export type ThreadItemProps = ThreadItem.Props & ReduxProps;
export const ThreadItem: React.FC<ThreadItemProps> = ({ thread, setSelectedThread }) => {
  const {
    stylingConfig: { columnReplyCounts, hideTags },
  } = useContext(InboxContext);
  const { loanId } = useParams<{ loanId: string }>();

  const renderTagState = () => {
    const lastRepliedBy = thread.lastRepliedBy;
    const messageState = threadHasANewMessage(lastRepliedBy.entityType) ? 'new' : 'awaiting';
    const messageTagState = TAG_STATE[messageState];

    return (
      <div className={css(styles.textMarginBottom)} data-it="Thread-TagState">
        <Tag text={messageTagState.copy} theme={messageTagState.theme} />
      </div>
    );
  };

  const getThreadBorderStyles = (): { borderLeft: string } => {
    const hasNewThreadMessage = threadHasANewMessage(thread.lastRepliedBy.entityType);
    return hasNewThreadMessage ? styles.newMsgBorder : styles.awaitingMsgBorder;
  };

  const getReplyCount = () => {
    const threadReplies = thread.messageCount - 1;
    return `${threadReplies} Repl${threadReplies === 1 ? 'y' : 'ies'}`;
  };

  const getAttachmentCount = () => {
    return `${thread.attachmentCount} Attachment${thread.attachmentCount > 1 ? 's' : ''}`;
  };

  const renderRowCountsWrapper = () => {
    return (
      <div className={css(styles.rowCountsWrapper)} data-it="ThreadItem-CountsWrapper">
        {thread.attachmentCount > 0 && (
          <div className={css(styles.countWrapper)}>
            <Text colorName="NIGHT_80" data-it="Thread-AttachmentCount" fontSize="md">
              {getAttachmentCount()}
            </Text>
          </div>
        )}
        <div className={css(styles.countWrapper)}>
          <Text colorName="NIGHT_80" data-it="Thread-ReplyCount" fontSize="md">
            {getReplyCount()}
          </Text>
        </div>
      </div>
    );
  };

  const renderColumnCountsWrapper = () => {
    return (
      <div className={css(styles.columnContentWrapper)} data-it="ThreadItem-CountsWrapper">
        <div className={css(styles.columnCountWrapper)}>
          <Text>{thread.messageCount - 1}</Text>
          <MaterialIcon color={BrandColors.NIGHT_80} size={18} type="message" />
        </div>

        <div className={css(styles.columnCountWrapper)}>
          <Text>{thread.attachmentCount}</Text>
          <MaterialIcon color={BrandColors.NIGHT_80} size={18} type="attach_file" />
        </div>
      </div>
    );
  };

  return (
    <div
      className={css(styles.threadWrapper, !thread.isResolved && getThreadBorderStyles())}
      onClick={() => setSelectedThread(thread.id, loanId)}
    >
      <div className={css(styles.contentWrapper)}>
        <div>
          {!thread.isResolved && renderTagState()}

          <div className={css(styles.textMarginBottom, styles.headerWrapper)}>
            <div className={css(styles.header)}>
              <Header size={5}>{truncate(thread.subject, { length: 40 })}</Header>
            </div>
            {!hideTags && <Tag data-it="ThreadItem-TopicTag" text={replace(thread.topic, '_', ' ')} theme="default" />}
          </div>

          <Text colorName="NIGHT_80" data-it="Thread-ReplyNameAndTime" fontSize="sm">
            {getReplyByNameAndTime(thread.lastRepliedBy)}
          </Text>
        </div>

        {!columnReplyCounts && renderRowCountsWrapper()}
      </div>

      {columnReplyCounts && renderColumnCountsWrapper()}
    </div>
  );
};
